import axios, { AxiosInstance } from "axios";
import {
  ApplicationsClient,
  AprClient,
  AuthClient,
  ClassClient,
  ClientSurveyExportClient,
  CohortsClient,
  CollegesClient,
  CommentsClient,
  CommonClient,
  ContributorEmploymentsClient,
  ContributorMatchServicesClient,
  ContributorMatchesClient,
  ContributorsClient,
  CustomDataModelFieldSelectListItemsClient,
  CustomDataModelFieldsClient,
  DashboardReportClient,
  DataCenterFileTasksClient,
  DataCenterFilesClient,
  DashboardTabClient,
  DevelopersClient,
  DiplomaTypesClient,
  DisciplinesClient,
  DistrictsClient,
  EligibilityDashboardClient,
  EndReasonsClient,
  ExportsClient,
  FeederSchoolsClient,
  FafsaDashboardClient,
  FileCategoriesClient,
  IEPClient,
  LEPClient,
  GenderDashboardClient,
  GradesClient,
  HomeClient,
  MatchReportsClient,
  NonPersonnelsClient,
  NotificationsClient,
  NscCollegesClient,
  ObjectiveReportsClient,
  PartnerIndirectCostsClient,
  PartnersClient,
  ProviderEmploymentsClient,
  ProvidersClient,
  PublicApplicationClient,
  PublicSurveyClient,
  ScholarshipPaymentMethodClient,
  ScholarshipStatusClient,
  RegionsClient,
  ScholarshipTypesClient,
  SchoolsClient,
  SearchClient,
  StaffClient,
  StaffEmploymentsClient,
  StaffPositionsClient,
  StaffReportsClient,
  StaffServiceTypesClient,
  StaffServicesClient,
  StudentAbsencesClient,
  StudentApplicationsClient,
  StudentDiplomasClient,
  StudentEnrollmentsClient,
  StudentFafsasClient,
  StudentFilesClient,
  StudentGpasClient,
  StudentGradesClient,
  StudentGroupsClient,
  StudentParentsClient,
  StudentPostHighSchoolsClient,
  StudentReportsClient,
  StudentScholarshipsClient,
  StudentServiceIndexesClient,
  StudentServiceSubTypesClient,
  StudentServiceTemplatesClient,
  StudentServiceTypesClient,
  StudentServicesClient,
  StudentTestsClient,
  StudentsClient,
  SubPartnersClient,
  SuppliesDetailsClient,
  SurveyExportClient,
  SurveyResponsesClient,
  SurveysClient,
  TestsClient,
  TextingConversationsClient,
  TextingOptInClient,
  UserManualClient,
  UserSettingClient,
  UsersClient,
  TopicsClient,
  PostHSPlanStatusClient,
  TestTypesClient,
  TestLevelClient,
  SurveyReportsClient,
  AuthV2Client,
  OpenAiClient,
  EmailClient,
} from "generated/openApi";
import { InterceptorCoordinator } from "utils/InterceptorCoordinator";

export type AiSearchFavoriteDto = {
  id: number;
  name: string;
  query: string;
  searchContext: string;
  isFavorite: boolean;
  useCount: number;
  displayOrder?: number;
  createdDate?: string;
  modifiedDate?: string;
  lastUsedDate?: string;
};

export type AiSearchFavoriteEditDto = {
  id?: number;
  name: string;
  query: string;
  searchContext?: string;
  isFavorite?: boolean;
  displayOrder?: number;
};

export default class ApiService {
  static apiEndpointHost: string | undefined = process.env.REACT_APP_API_ENDPOINT;

  static instance: AxiosInstance = axios.create({
    timeout: 360000,
    baseURL: process.env.REACT_APP_API_ENDPOINT,
    withCredentials: false,
    // This no longer works with NSWAG v14+
    // transformResponse: [], // don't transform the response into JSON, let the openAPI client do the parsing
  });

  static initializeApi() {
    InterceptorCoordinator.initialize(this.instance);
  }

  static studentServiceIndexes = new StudentServiceIndexesClient(this.apiEndpointHost, this.instance);
  static tests = new TestsClient(this.apiEndpointHost, this.instance);
  static home = new HomeClient(this.apiEndpointHost, this.instance);
  static auth = new AuthClient(this.apiEndpointHost, this.instance);
  static authV2 = new AuthV2Client(this.apiEndpointHost, this.instance);
  static userSetting = new UserSettingClient(this.apiEndpointHost, this.instance);
  static common = new CommonClient(this.apiEndpointHost, this.instance);
  static search = new SearchClient(this.apiEndpointHost, this.instance);
  static developers = new DevelopersClient(this.apiEndpointHost, this.instance);
  static users = new UsersClient(this.apiEndpointHost, this.instance);
  static contributorMatchServices = new ContributorMatchServicesClient(this.apiEndpointHost, this.instance);
  static suppliesDetails = new SuppliesDetailsClient(this.apiEndpointHost, this.instance);
  static contributors = new ContributorsClient(this.apiEndpointHost, this.instance);
  static contributorMatches = new ContributorMatchesClient(this.apiEndpointHost, this.instance);
  static matchReports = new MatchReportsClient(this.apiEndpointHost, this.instance);
  static contributorEmployments = new ContributorEmploymentsClient(this.apiEndpointHost, this.instance);
  static partners = new PartnersClient(this.apiEndpointHost, this.instance);
  static partnerIndirectCosts = new PartnerIndirectCostsClient(this.apiEndpointHost, this.instance);
  static subPartners = new SubPartnersClient(this.apiEndpointHost, this.instance);
  static nonPersonnels = new NonPersonnelsClient(this.apiEndpointHost, this.instance);
  static dataCenterFiles = new DataCenterFilesClient(this.apiEndpointHost, this.instance);
  static dataCenterFileTasks = new DataCenterFileTasksClient(this.apiEndpointHost, this.instance);
  static students = new StudentsClient(this.apiEndpointHost, this.instance);
  static studentServices = new StudentServicesClient(this.apiEndpointHost, this.instance);
  static studentServiceTemplates = new StudentServiceTemplatesClient(this.apiEndpointHost, this.instance);
  static studentEnrollments = new StudentEnrollmentsClient(this.apiEndpointHost, this.instance);
  static studentGrades = new StudentGradesClient(this.apiEndpointHost, this.instance);
  static studentGroups = new StudentGroupsClient(this.apiEndpointHost, this.instance);
  static studentApplications = new StudentApplicationsClient(this.apiEndpointHost, this.instance);
  static userManual = new UserManualClient(this.apiEndpointHost, this.instance);
  static studentFiles = new StudentFilesClient(this.apiEndpointHost, this.instance);
  static exports = new ExportsClient(this.apiEndpointHost, this.instance);
  static studentGpas = new StudentGpasClient(this.apiEndpointHost, this.instance);
  static studentAbsences = new StudentAbsencesClient(this.apiEndpointHost, this.instance);
  static studentTests = new StudentTestsClient(this.apiEndpointHost, this.instance);
  static studentDiplomas = new StudentDiplomasClient(this.apiEndpointHost, this.instance);
  static studentPostHighSchools = new StudentPostHighSchoolsClient(this.apiEndpointHost, this.instance);
  static studentScholarships = new StudentScholarshipsClient(this.apiEndpointHost, this.instance);
  static studentFafsas = new StudentFafsasClient(this.apiEndpointHost, this.instance);
  static studentParents = new StudentParentsClient(this.apiEndpointHost, this.instance);
  static comments = new CommentsClient(this.apiEndpointHost, this.instance);
  static customDataModelFields = new CustomDataModelFieldsClient(this.apiEndpointHost, this.instance);
  static customDataModelFieldSelectListItems = new CustomDataModelFieldSelectListItemsClient(
    this.apiEndpointHost,
    this.instance
  );
  static publicSurvey = new PublicSurveyClient(this.apiEndpointHost, this.instance);
  static openAi = new OpenAiClient(this.apiEndpointHost, this.instance);
  static studentReports = new StudentReportsClient(this.apiEndpointHost, this.instance);
  static publicApplication = new PublicApplicationClient(this.apiEndpointHost, this.instance);
  static surveyExport = new SurveyExportClient(this.apiEndpointHost, this.instance);
  static clientSurveyExport = new ClientSurveyExportClient(this.apiEndpointHost, this.instance);
  static surveyResponses = new SurveyResponsesClient(this.apiEndpointHost, this.instance);
  static surveys = new SurveysClient(this.apiEndpointHost, this.instance);
  static applications = new ApplicationsClient(this.apiEndpointHost, this.instance);
  static schools = new SchoolsClient(this.apiEndpointHost, this.instance);
  static districts = new DistrictsClient(this.apiEndpointHost, this.instance);
  static endReasons = new EndReasonsClient(this.apiEndpointHost, this.instance);
  static scholarshipPaymentMethod = new ScholarshipPaymentMethodClient(this.apiEndpointHost, this.instance);
  static scholarshipStatus = new ScholarshipStatusClient(this.apiEndpointHost, this.instance);
  static scholarshipTypes = new ScholarshipTypesClient(this.apiEndpointHost, this.instance);
  static studentServiceTypes = new StudentServiceTypesClient(this.apiEndpointHost, this.instance);
  static studentServiceSubTypes = new StudentServiceSubTypesClient(this.apiEndpointHost, this.instance);
  static colleges = new CollegesClient(this.apiEndpointHost, this.instance);
  static cohorts = new CohortsClient(this.apiEndpointHost, this.instance);
  static dashboardReport = new DashboardReportClient(this.apiEndpointHost, this.instance);
  static providers = new ProvidersClient(this.apiEndpointHost, this.instance);
  static classes = new ClassClient(this.apiEndpointHost, this.instance);
  static providerEmployments = new ProviderEmploymentsClient(this.apiEndpointHost, this.instance);
  static grades = new GradesClient(this.apiEndpointHost, this.instance);
  static apr = new AprClient(this.apiEndpointHost, this.instance);
  static nscColleges = new NscCollegesClient(this.apiEndpointHost, this.instance);
  static objectiveReports = new ObjectiveReportsClient(this.apiEndpointHost, this.instance);
  static staff = new StaffClient(this.apiEndpointHost, this.instance);
  static staffServices = new StaffServicesClient(this.apiEndpointHost, this.instance);
  static staffEmployments = new StaffEmploymentsClient(this.apiEndpointHost, this.instance);
  static staffReports = new StaffReportsClient(this.apiEndpointHost, this.instance);
  static staffServiceTypes = new StaffServiceTypesClient(this.apiEndpointHost, this.instance);
  static staffPositions = new StaffPositionsClient(this.apiEndpointHost, this.instance);
  static textingConversations = new TextingConversationsClient(this.apiEndpointHost, this.instance);
  static textingOptIn = new TextingOptInClient(this.apiEndpointHost, this.instance);
  static fileCategories = new FileCategoriesClient(this.apiEndpointHost, this.instance);
  static feederSchools = new FeederSchoolsClient(this.apiEndpointHost, this.instance);
  static diplomaTypes = new DiplomaTypesClient(this.apiEndpointHost, this.instance);
  static topics = new TopicsClient(this.apiEndpointHost, this.instance);
  static disciplines = new DisciplinesClient(this.apiEndpointHost, this.instance);
  static lep = new LEPClient(this.apiEndpointHost, this.instance);
  static iep = new IEPClient(this.apiEndpointHost, this.instance);
  static regions = new RegionsClient(this.apiEndpointHost, this.instance);
  static postHSPlanStatus = new PostHSPlanStatusClient(this.apiEndpointHost, this.instance);
  static testTypes = new TestTypesClient(this.apiEndpointHost, this.instance);
  static testLevels = new TestLevelClient(this.apiEndpointHost, this.instance);
  static notifications = new NotificationsClient(this.apiEndpointHost, this.instance);
  static fafsaDashboard = new FafsaDashboardClient(this.apiEndpointHost, this.instance);
  static genderDashboard = new GenderDashboardClient(this.apiEndpointHost, this.instance);
  static eligibilityDashboard = new EligibilityDashboardClient(this.apiEndpointHost, this.instance);
  static surveyReports = new SurveyReportsClient(this.apiEndpointHost, this.instance);
  static dashboardTab = new DashboardTabClient(this.apiEndpointHost, this.instance);
  static email = new EmailClient(this.apiEndpointHost, this.instance);
  static aiSearchFavorites = {
    getList: async (searchContext = "StudentFilters") => {
      const response = await ApiService.instance.get<AiSearchFavoriteDto[]>("/AiSearchFavorites/GetList", {
        params: { searchContext },
      });
      return response.data;
    },
    saveCreate: async (favorite: AiSearchFavoriteEditDto) => {
      const response = await ApiService.instance.post<AiSearchFavoriteDto>("/AiSearchFavorites/SaveCreate", favorite);
      return response.data;
    },
    saveUpdate: async (favorite: AiSearchFavoriteEditDto) => {
      const response = await ApiService.instance.post<AiSearchFavoriteDto>("/AiSearchFavorites/SaveUpdate", favorite);
      return response.data;
    },
    saveOrder: async (favorites: { id: number; displayOrder: number }[]) => {
      await ApiService.instance.post("/AiSearchFavorites/SaveOrder", favorites);
    },
    markUsed: async (id: number) => {
      const response = await ApiService.instance.post<AiSearchFavoriteDto>("/AiSearchFavorites/MarkUsed", null, {
        params: { id },
      });
      return response.data;
    },
    delete: async (id: number) => {
      await ApiService.instance.delete(`/AiSearchFavorites/${id}`);
    },
  };
}
